import React, { useEffect, useMemo, useState } from 'react';

import { api } from '../../api/client';
import { API_ITEMS_URL } from '../../utils';
import { composeBlockQuery } from './queryBlockQuery';
import { elementLabel, formatValue } from '../items/itemValueDisplay';
import useLinkedItems from '../../hooks/useLinkedItems';
import useEnsureCards from '../../hooks/useEnsureCards';
import getItemDisplayValue from '../../utils/items';
import { createLookupResolver, omitUnresolvedLookups } from '../../utils/lookups';
import { normalizeId } from '../../utils/ids';
import { elementRefusalMessage } from '../../utils/elementRefusal';
import { Skeleton } from '../loading';

const EMPTY_ROWS = [];

/**
 * A panel of OTHER records on a page: "this resident's open to-dos", "next
 * fortnight's meetings", "the psychotropics".
 *
 * The query is composed at render time (queryBlockQuery.js) rather than
 * stored, because a block on a record layout points back at the record being
 * printed — and because a card-tier layout has no collection, so its KQL is
 * only ever compiled against whichever collection the sheet is printed from.
 * A condition that names nothing there selects nothing there, which is the
 * right answer for a card used in a dozen collections.
 *
 * Rows are real `<table>` rows when the block asks for a table, because that
 * is what makes a header repeat across printed pages — the one thing a div
 * grid cannot do.
 *
 * Every cell — table, grid and list alike — goes through the one shared
 * renderer WITH a lookup resolver, so a column of choices prints the author's
 * words and a column of references prints titles. The panel used to spend the
 * resolver on the row title alone, which is why a printed sheet carried slugs
 * in its columns.
 *
 * That resolver is the panel's OWN. The page hands one down, but it was built
 * from the pool of the record being printed (PrintPage seeds useLinkedItems
 * with that one item), and these rows are records the page has never seen —
 * so every reference in them misses, which would leave a lookup column blank
 * beside a title carrying the raw id. The panel therefore pools its own rows
 * through the same read-scoped `?ids=` batch every other surface uses, and
 * falls back to the page's resolver for anything its own pool has not
 * delivered. Title and cells read through the same omit rule, so a reference
 * that is genuinely unreadable reads as `[Trailer]` in the title and as blank
 * in the cell — never as an ObjectId on paper.
 */
export default function QueryBlock({
	block,
	item,
	collectionId,
	cardsById,
	getElementsForCard,
	resolveLookupValue,
	unitSystem = 'default',
	onLoaded,
}) {
	const [rows, setRows] = useState(null);
	useEnsureCards(rows); // rows of a card this session has not seen yet
	const [failed, setFailed] = useState(false);

	const query = useMemo(() => composeBlockQuery({
		blockQuery: block?.query,
		linkElement: block?.linkElement,
		itemId: normalizeId(item?._id),
	}), [block, item]);

	useEffect(() => {
		if (!query || !collectionId) { setRows([]); return undefined; }
		let active = true;
		api.get(API_ITEMS_URL, {
			params: { collection_id: collectionId, q: query, limit: block?.limit || 50 },
		})
			.then(({ data }) => {
				if (!active) return;
				const list = Array.isArray(data) ? data : (data?.items || []);
				setRows(list);
				setFailed(false);
			})
			// A panel that cannot load leaves the sheet printable: the rest of
			// the record is still worth having on paper.
			// An element the block's query could mean several of (element ids
			// step 3: text saved before keepr named elements by id) says what
			// it could be, so the layout's editor knows what to fix.
			.catch((error) => { if (active) { setRows([]); setFailed(elementRefusalMessage(error) || true); } })
			.finally(() => { if (active) onLoaded?.(); });
		return () => { active = false; };
	}, [query, collectionId, block, onLoaded]);

	const columns = useMemo(() => {
		const names = Array.isArray(block?.columns) ? block.columns : [];
		if (!names.length || !rows?.length) return [];
		const defs = getElementsForCard ? getElementsForCard(rows[0].card_id) : [];
		const byName = new Map((defs || []).map(def => [def.name, def]));
		return names.map(name => byName.get(name)).filter(Boolean);
	}, [block, rows, getElementsForCard]);

	// The panel's own pool, seeded with the rows it just fetched, so their
	// references resolve. `enabled` waits for card definitions: without them
	// collectLookupIds finds nothing and the batch would never be asked for.
	const seedRows = rows || EMPTY_ROWS;
	const linked = useLinkedItems({
		items: seedRows,
		getElementsForCard,
		enabled: seedRows.length > 0 && typeof getElementsForCard === 'function',
		resetKey: `${block?.id || 'query'}:${query}`,
	});
	const resolveRowLookup = useMemo(() => {
		const own = omitUnresolvedLookups(createLookupResolver({
			itemsById: linked,
			cardsById,
			getElementsForCard,
		}));
		const page = omitUnresolvedLookups(resolveLookupValue);
		// The page's pool covers the printed record's own references, which a
		// row may well share; asking it second costs nothing and resolves a
		// reference a beat sooner. Both omit, so neither can return an id.
		return rawValue => own(rawValue) || page(rawValue);
	}, [linked, cardsById, getElementsForCard, resolveLookupValue]);

	const titleOf = (row) => {
		const card = cardsById?.[normalizeId(row?.card_id)] || null;
		return getItemDisplayValue({
			card,
			elementDefs: getElementsForCard ? getElementsForCard(row?.card_id) : [],
			item: row,
			// The SAME resolver the cells use. A title composed from a template
			// that nests a lookup ("{{trailer}} — {{inspection-type}}") used to
			// print the unresolved id straight onto the sheet; omitted, the
			// title's own empty-element placeholder names the element instead.
			resolveLookupValue: resolveRowLookup,
		});
	};

	// While the rows are out, a placeholder in the panel's footprint — on screen
	// only (`print-hide`): a sheet is only ever printed once `onLoaded` has fired.
	if (rows === null) return <div className="print-hide"><Skeleton variant="list-row" count={3} /></div>;
	if (!rows.length) {
		return (
			<div className="page-query__empty">
				{failed ? (typeof failed === 'string' ? `This panel could not be loaded: ${failed}` : 'This panel could not be loaded.') : 'Nothing to show.'}
			</div>
		);
	}

	if (block.render === 'table' && columns.length) {
		return (
			<table className="page-query__table">
				<thead>
					<tr>
						<th>Item</th>
						{columns.map(def => <th key={def.name}>{elementLabel(def)}</th>)}
					</tr>
				</thead>
				<tbody>
					{rows.map(row => (
						<tr key={row._id}>
							<td>{titleOf(row)}</td>
							{columns.map(def => (
								<td key={def.name}>{formatValue(def, row?.elements?.[def.name], { unitSystem, resolveLookupValue: resolveRowLookup }) || ''}</td>
							))}
						</tr>
					))}
				</tbody>
			</table>
		);
	}

	if (block.render === 'grid') {
		return (
			<div className="page-query__grid">
				{rows.map(row => (
					<div className="page-query__cell" key={row._id}>
						<div className="page-query__cell-title">{titleOf(row)}</div>
						{columns.map(def => (
							<div className="page-query__cell-line" key={def.name}>
								<span className="page-query__cell-label">{elementLabel(def)}</span>
								{' '}
								{formatValue(def, row?.elements?.[def.name], { unitSystem, resolveLookupValue: resolveRowLookup }) || ''}
							</div>
						))}
					</div>
				))}
			</div>
		);
	}

	return (
		<ul className="page-query__list">
			{rows.map(row => (
				<li key={row._id}>
					{titleOf(row)}
					{columns.length ? (
						<span className="page-query__line-meta">
							{columns
								.map(def => formatValue(def, row?.elements?.[def.name], { unitSystem, resolveLookupValue: resolveRowLookup }))
								.filter(Boolean)
								.join(' · ')}
						</span>
					) : null}
				</li>
			))}
		</ul>
	);
}
